import { DownloadSimpleIcon, MagnifyingGlassIcon, UploadSimpleIcon } from '@phosphor-icons/react'
import { ChangeEvent, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useOutletContext } from 'react-router-dom'

import { useExportResidentParkings, useGetParkingSpots } from '@/api/parking-management'
import { GET_PARKING_SPOTS_BASE_QUERY_KEY } from '@/api/parking-management/get-parking-spots'
import { usePersonColumnSettings } from '@/components/business/person-column'
import { TableGroup } from '@/components/layout/table-group'
import { Button } from '@/components/ui/button'
import { EmptyState } from '@/components/ui/empty-state'
import { Icon } from '@/components/ui/icon'
import { Select } from '@/components/ui/select'
import { RowAction, SortOrder } from '@/components/ui/table'
import { TextInput } from '@/components/ui/text-input'
import { popupFactory } from '@/factories'
import { useFilters, useSearchParams } from '@/hooks'
import { ResidentParkingSpot } from '@/models'
import { selectActiveBuilding } from '@/reducers/buildingSelectorReducer'
import { downloadHref } from '@/utils'

import { PARKING_MANAGEMENT_TEMPLATE_PATH } from '../parking-management.page.constants'
import type { ParkingManagementOutletContext } from '../parking-management.types'
import { ParkingSpotModal } from './parking-modals'
import {
  getAssigneeTypeOptions,
  getResidentsParkingColumns,
  initialResidentsParkingFilters,
  ResidentsParkingFilters,
  residentsParkingQueryFiltersSchema,
} from './residents-parking.constants'
import * as S from './residents-parking.constants.styled'

export const ResidentsParkingPage = () => {
  const { t } = useTranslation(['parking-management'])
  const activeBuilding = useSelector(selectActiveBuilding)
  const assigneeColumnSettings = usePersonColumnSettings({
    column: 'assignee',
    dashboard: 'Parking',
    fieldType: 'resident',
    rowsQueryKey: [GET_PARKING_SPOTS_BASE_QUERY_KEY],
  })
  const { isUploadParkingCsvPending = false, onUploadCSV } =
    useOutletContext<ParkingManagementOutletContext>() ?? {}

  const searchParams = useSearchParams({
    initialState: initialResidentsParkingFilters,
    schema: residentsParkingQueryFiltersSchema,
  })

  const filters = useFilters<ResidentsParkingFilters>({
    controlledState: searchParams.parsedQuery,
    initialState: initialResidentsParkingFilters,
    onFiltersChange: searchParams.writeParams,
  })

  const sortBy = filters.getFilter('sortBy')
  const sortOrder = filters.getFilter('sortOrder')
  const rawSearch = filters.getFilter('search')
  const search = rawSearch?.trim()
  const skip = filters.getFilter('skip')
  const limit = filters.getFilter('limit')
  const assignmentType = filters.getFilter('assignmentType')

  const { data, error, isFetching, isPending } = useGetParkingSpots({
    assignmentType,
    buildingId: activeBuilding?.id,
    limit,
    order: sortOrder,
    search,
    skip,
    sort: sortBy,
  })

  const rows = data?.parkings || []
  const total = data?.count || 0
  const isInitialised = data?.isInitialised || false
  const { mutateAsync: exportResidentParkings, isPending: isExportResidentParkingsPending } =
    useExportResidentParkings()

  const handleSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
    filters.setFilter('search', e.target.value)
  }

  const handleSort = (nextSortBy: string | null, nextSortOrder: SortOrder | null) => {
    filters.setFilters({
      sortBy: nextSortBy ?? undefined,
      sortOrder: nextSortOrder ?? undefined,
    })
  }

  const handleAssigneeTypeChange = (value?: string) => {
    filters.setFilter('assignmentType', value)
  }

  const handleEdit = useCallback((row: ResidentParkingSpot) => {
    popupFactory.open(ParkingSpotModal, {
      parkingSpot: row,
    })
  }, [])

  const rowActions: RowAction<ResidentParkingSpot>[] = useMemo(
    () => [
      {
        label: t('parking-management:residentsParking.rowActions.edit'),
        onClick: handleEdit,
        primary: true,
      },
    ],
    [handleEdit, t],
  )

  const handleDownloadCSVClick = () => {
    if (!activeBuilding) return

    let fileName = 'parking-spots.csv'
    const trimmed = activeBuilding.name?.trim()
    if (trimmed) {
      fileName = `${trimmed}-` + fileName
    }

    void exportResidentParkings({
      buildingId: activeBuilding.id,
      fileName,
    })
  }

  const handleDownloadTemplate = () => {
    downloadHref(PARKING_MANAGEMENT_TEMPLATE_PATH, 'Parking_Management_Template.csv')
  }

  if (isPending || !isInitialised) {
    return (
      <S.Card loading={isPending}>
        <EmptyState
          title={t('parking-management:residentsParking.empty.title')}
          description={t('parking-management:residentsParking.empty.description')}
          image={{
            alt: t('parking-management:residentsParking.empty.imageAlt'),
            src: '/images/resident-parking/empty-resident-parking.svg',
            width: '30rem',
          }}
          actions={
            <>
              <Button use="secondary" onClick={handleDownloadTemplate}>
                <Icon icon={DownloadSimpleIcon} />
                {t('parking-management:residentsParking.downloadTemplate')}
              </Button>
              <Button
                use="primary"
                disabled={!activeBuilding?.id || !onUploadCSV}
                loading={isUploadParkingCsvPending}
                onClick={onUploadCSV}
              >
                <Icon icon={UploadSimpleIcon} />
                {t('parking-management:residentsParking.uploadCsv')}
              </Button>
            </>
          }
        />
      </S.Card>
    )
  }

  return (
    <TableGroup
      id="residents-parking"
      columns={getResidentsParkingColumns(t, assigneeColumnSettings)}
      data={rows}
      error={error}
      getRowKey={(item) => item.id}
      loading={isFetching}
      navigation={{
        actions: (
          <Button
            onlyBox
            use="secondary"
            size="small"
            title={t('parking-management:residentsParking.exportCsv')}
            loading={isExportResidentParkingsPending}
            disabled={!activeBuilding}
            onClick={handleDownloadCSVClick}
          >
            <Icon icon={DownloadSimpleIcon} />
          </Button>
        ),
        filters: (
          <Select<string>
            options={getAssigneeTypeOptions(t)}
            placeholder={t('parking-management:residentsParking.filters.availability.placeholder')}
            size="small"
            value={assignmentType ?? null}
            onChange={handleAssigneeTypeChange}
            onReset={() => handleAssigneeTypeChange(undefined)}
          />
        ),
        search: (
          <TextInput
            clearable
            icon={MagnifyingGlassIcon}
            placeholder={t('parking-management:residentsParking.search.placeholder')}
            size="small"
            value={rawSearch}
            onChange={handleSearchChange}
          />
        ),
      }}
      pagination={{
        paginationFilters: filters,
        total,
      }}
      sortBy={sortBy}
      sortOrder={sortOrder}
      tableFilters={filters}
      rowActions={rowActions}
      onSort={handleSort}
    />
  )
}
